import { MoreVert } from '@mui/icons-material';
import { IconButton, MenuItem } from '@mui/material';
import {
  NotificationTypeEnum,
  useMenu,
  useNotification,
} from '@prismamedia/one-components';
import type { FC, MouseEvent } from 'react';
import { useCallback } from 'react';
import { useUpdatePageCategoryOrder } from '../../../../../apollo/mutations/category.web.graphql';
import type { CategoryList } from '../../../../../types';
import {
  MORE_ACTIONS_LABEL,
  ORDER_UPDATE_ERROR_MESSAGE,
  PLACE_HIGHER_LABEL,
  PLACE_LOWER_LABEL,
} from './constants';

interface CategoryCardMenuProps {
  category: CategoryList;
}

export const CategoryCardMenu: FC<CategoryCardMenuProps> = ({ category }) => {
  const { closeMenu, openMenu } = useMenu();
  const { pushNotification } = useNotification();
  const { isUpdatingOrder, updateOrder } = useUpdatePageCategoryOrder();

  const notifyOrderError = useCallback(() => {
    pushNotification({
      message: ORDER_UPDATE_ERROR_MESSAGE,
      type: NotificationTypeEnum.error,
    });
  }, [pushNotification]);

  const placeCategory = useCallback(
    async (nextOrder: number) => {
      if (nextOrder < 0) return;

      closeMenu();

      try {
        const result = await updateOrder(category.id, nextOrder);
        if (!result.data?.updateCategory) notifyOrderError();
      } catch {
        notifyOrderError();
      }
    },
    [category.id, closeMenu, notifyOrderError, updateOrder],
  );

  const handleOpenMenu = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    openMenu(
      event.currentTarget,
      <>
        <MenuItem
          disabled={category.order === 0}
          onClick={() => placeCategory(category.order - 1)}
        >
          {PLACE_HIGHER_LABEL}
        </MenuItem>
        <MenuItem onClick={() => placeCategory(category.order + 1)}>
          {PLACE_LOWER_LABEL}
        </MenuItem>
      </>,
    );
  };

  return (
    <IconButton
      aria-label={MORE_ACTIONS_LABEL}
      disabled={isUpdatingOrder}
      onClick={handleOpenMenu}
      size="small"
      sx={{ color: 'grey.600' }}
    >
      <MoreVert fontSize="small" />
    </IconButton>
  );
};
